import { useState } from 'react';
import type { User } from '@supabase/supabase-js';
import { supabase } from '../../../lib/supabase';

interface PersonalInfoSectionProps {
  user: User;
  onUserUpdate: (user: User) => void;
}

const PersonalInfoSection = ({
  user,
  onUserUpdate,
}: PersonalInfoSectionProps) => {
  const [fullName, setFullName] = useState(
    user.user_metadata?.full_name || ''
  );
  const [phone, setPhone] = useState(user.user_metadata?.phone || '');
  const [dateOfBirth, setDateOfBirth] = useState(
    user.user_metadata?.date_of_birth || ''
  );
  const [address, setAddress] = useState(
    user.user_metadata?.address || ''
  );
  const [city, setCity] = useState(user.user_metadata?.city || '');
  const [state, setState] = useState(user.user_metadata?.state || '');
  const [zipCode, setZipCode] = useState(
    user.user_metadata?.zip_code || ''
  );
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<
    { type: 'success' | 'error'; text: string } | null
  >(null);

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setMessage(null);

    try {
      const { data, error } = await supabase.auth.updateUser({
        data: {
          full_name: fullName,
          phone,
          date_of_birth: dateOfBirth,
          address,
          city,
          state,
          zip_code: zipCode,
        },
      });

      if (error) throw error;
      if (data.user) onUserUpdate(data.user);
      setMessage({
        type: 'success',
        text: 'Profile updated successfully!',
      });
      setTimeout(() => setMessage(null), 3000);
    } catch (err: any) {
      setMessage({
        type: 'error',
        text: err.message || 'Failed to update profile.',
      });
    } finally {
      setSaving(false);
    }
  };

  const getInitials = () => {
    const name = fullName || user.email || '';
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-6 md:p-8">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-8 h-8 flex items-center justify-center">
          <i className="ri-user-3-line text-xl text-[#f04e58]"></i>
        </div>
        <h2 className="text-lg font-semibold text-gray-900">
          Personal Information
        </h2>
      </div>

      {message && (
        <div
          className={`mb-6 p-3 rounded-lg text-sm ${
            message.type === 'success'
              ? 'bg-green-50 text-green-700 border border-green-200'
              : 'bg-red-50 text-red-700 border border-red-200'
          }`}
        >
          <div className="flex items-center gap-2">
            <i
              className={
                message.type === 'success'
                  ? 'ri-check-line'
                  : 'ri-error-warning-line'
              }
            ></i>
            {message.text}
          </div>
        </div>
      )}

      {/* Avatar */}
      <div className="flex items-center gap-5 mb-8 pb-6 border-b border-gray-100">
        <div className="w-20 h-20 rounded-full bg-[#f04e58] flex items-center justify-center text-white text-2xl font-semibold flex-shrink-0">
          {getInitials()}
        </div>
        <div>
          <p className="text-base font-medium text-gray-900">
            {fullName || 'Your Name'}
          </p>
          <p className="text-sm text-gray-500 mt-0.5">{user.email}</p>
        </div>
      </div>

      <form onSubmit={handleSave} className="space-y-5">
        {/* Full Name */}
        <div>
          <label
            htmlFor="profileFullName"
            className="block text-sm font-medium text-gray-700 mb-1.5"
          >
            Full Name <span className="text-red-400">*</span>
          </label>
          <input
            id="profileFullName"
            type="text"
            value={fullName}
            onChange={(e) => setFullName(e.target.value)}
            required
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#f04e58]/20 focus:border-[#f04e58] outline-none transition-all text-sm"
            placeholder="John Doe"
          />
        </div>

        {/* Email (read-only) */}
        <div>
          <label
            htmlFor="profileEmail"
            className="block text-sm font-medium text-gray-700 mb-1.5"
          >
            Email Address
          </label>
          <div className="relative">
            <input
              id="profileEmail"
              type="email"
              value={user.email || ''}
              readOnly
              className="w-full px-4 py-3 border border-gray-200 rounded-lg bg-gray-50 text-gray-500 text-sm cursor-not-allowed"
            />
            <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-1 text-xs text-green-600">
              <i className="ri-verified-badge-fill"></i>
              Verified
            </div>
          </div>
        </div>

        {/* Phone & DOB */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <div>
            <label
              htmlFor="profilePhone"
              className="block text-sm font-medium text-gray-700 mb-1.5"
            >
              Phone Number
            </label>
            <input
              id="profilePhone"
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#f04e58]/20 focus:border-[#f04e58] outline-none transition-all text-sm"
              placeholder="+1 (555) 000-0000"
            />
          </div>
          <div>
            <label
              htmlFor="profileDob"
              className="block text-sm font-medium text-gray-700 mb-1.5"
            >
              Date of Birth
            </label>
            <input
              id="profileDob"
              type="date"
              value={dateOfBirth}
              onChange={(e) => setDateOfBirth(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#f04e58]/20 focus:border-[#f04e58] outline-none transition-all text-sm"
            />
          </div>
        </div>

        {/* Address */}
        <div>
          <label
            htmlFor="profileAddress"
            className="block text-sm font-medium text-gray-700 mb-1.5"
          >
            Address
          </label>
          <input
            id="profileAddress"
            type="text"
            value={address}
            onChange={(e) => setAddress(e.target.value)}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#f04e58]/20 focus:border-[#f04e58] outline-none transition-all text-sm"
            placeholder="123 Main Street"
          />
        </div>

        {/* City, State, Zip */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          <div>
            <label
              htmlFor="profileCity"
              className="block text-sm font-medium text-gray-700 mb-1.5"
            >
              City
            </label>
            <input
              id="profileCity"
              type="text"
              value={city}
              onChange={(e) => setCity(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#f04e58]/20 focus:border-[#f04e58] outline-none transition-all text-sm"
              placeholder="New York"
            />
          </div>
          <div>
            <label
              htmlFor="profileState"
              className="block text-sm font-medium text-gray-700 mb-1.5"
            >
              State
            </label>
            <input
              id="profileState"
              type="text"
              value={state}
              onChange={(e) => setState(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#f04e58]/20 focus:border-[#f04e58] outline-none transition-all text-sm"
              placeholder="NY"
            />
          </div>
          <div>
            <label
              htmlFor="profileZip"
              className="block text-sm font-medium text-gray-700 mb-1.5"
            >
              ZIP Code
            </label>
            <input
              id="profileZip"
              type="text"
              value={zipCode}
              onChange={(e) => setZipCode(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#f04e58]/20 focus:border-[#f04e58] outline-none transition-all text-sm"
              placeholder="10001"
            />
          </div>
        </div>

        {/* Save */}
        <div className="flex justify-end pt-2">
          <button
            type="submit"
            disabled={saving}
            className="inline-flex items-center gap-2 px-6 py-2.5 bg-[#f04e58] text-white text-sm font-medium rounded-lg hover:bg-[#d93d47] transition-all disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap"
          >
            {saving ? (
              <>
                <i className="ri-loader-4-line animate-spin"></i>
                Saving...
              </>
            ) : (
              <>
                <i className="ri-check-line"></i>
                Save Changes
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default PersonalInfoSection;
